import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { PERIOADE_PACHET, TIPURI_PACHET, eticheta } from "./enums.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

const ADMINISTRARE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR"];


/** Cantitățile se scriu cu virgulă, ca peste tot în aplicație. */
const cant = (n) => Number(n ?? 0).toLocaleString("ro-RO", { maximumFractionDigits: 2 });

/**
 * Contorul unui client: cât include abonamentul și cât a mai rămas.
 *
 * Se pune pe fișa clientului, unde se poartă discuția de facturare — nu într-un
 * ecran separat pe care nu-l deschide nimeni.
 */
export default function ContorAbonament({ eu, client, contracte }) {
  const [contoare, setContoare] = useState(null);
  const [eroare, setEroare] = useState("");
  const [formular, setFormular] = useState(false);
  const poateAdministra = ADMINISTRARE.includes(eu.rol);

  const reincarca = useCallback(async () => {
    try { setContoare(await api.contorClient(client.id)); setEroare(""); }
    catch (e) { setEroare(mesajEroare(e)); setContoare([]); }
  }, [client.id]);

  useEffect(() => { reincarca(); }, [reincarca]);

  return (
    <div className="panou">
      <div className="panou-antet">
        <h2>{tr("Contorul de abonament")}</h2>
        <span className="numar">{contoare?.length ?? 0}</span>
        {poateAdministra && contracte.length > 0 && (
          <button className="buton-mic" onClick={() => setFormular(true)}>{tr("adaugă pachet")}</button>
        )}
      </div>
      <div className="panou-corp">
        {eroare && <div className="eroare">{eroare}</div>}

        {contoare === null ? (
          <div className="gol">{tr("Se încarcă…")}</div>
        ) : contoare.length === 0 ? (
          <div className="gol">
            {tr("Niciun pachet. Fără el, tot ce se lucrează la acest client se facturează separat — nu există „4 vizite incluse\" de urmărit.")}
            {contracte.length === 0 && " Un pachet se leagă de un contract, iar clientul n-are niciunul încă."}
          </div>
        ) : (
          contoare.map((c) => <Contor key={c.id} contor={c} />)
        )}
      </div>

      {formular && (
        <FormPachet contracte={contracte} inchide={() => setFormular(false)}
          gata={async () => { setFormular(false); await reincarca(); }} />
      )}
    </div>
  );
}

/**
 * Bara de contor e folosită și de portalul clientului (MT15). Exportată, nu
 * copiată: două bare care trebuie să arate la fel, dar sunt scrise separat,
 * ajung să nu mai arate la fel.
 */
export function Contor({ contor }) {
  const c = contor.curent;
  if (!c) return null;
  const procent = Math.min(100, c.procent);
  const stare = c.depasire > 0 ? "depasit" : c.procent >= 80 ? "aproape" : "bine";

  return (
    <div className="contor">
      <div className="contor-antet">
        <strong>{tr(contor.denumire)}</strong>
        <span className="cod-mic">
          {eticheta(TIPURI_PACHET, contor.tip)} · {eticheta(PERIOADE_PACHET, contor.perioada)}
          {contor.contract?.numar ? ` · ${contor.contract.numar}` : ""}
        </span>
      </div>

      <div className={`contor-bara contor-${stare}`}>
        <div className="contor-umplut" style={{ width: `${procent}%` }} />
      </div>

      <div className="contor-cifre">
        <span><strong>{cant(c.consumat)}</strong> {tr("consumat")}</span>
        <span><strong>{cant(c.ramas)}</strong> {tr("rămas")}</span>
        <span>{tr("din")} {cant(c.disponibil)}</span>
        {c.reportat > 0 && <span className="cod-mic">{tr("(inclusiv")} {cant(c.reportat)} {tr("reportat)")}</span>}
        {c.depasire > 0 && (
          <span className="marcaj marcaj-atentie">
            {cant(c.depasire)} {tr("peste pachet — se facturează separat")}
          </span>
        )}
      </div>

      <div className="cod-mic">
        {tr("perioada curentă:")} {data(c.perioada.deLa)} → {data(c.perioada.panaLa)}
      </div>
    </div>
  );
}

function FormPachet({ contracte, inchide, gata }) {
  const [f, setF] = useState({
    contractId: contracte[0]?.id ?? "", denumire: "", tip: "OreManopera",
    cantitateInclusa: "", perioada: "Anual", reportabil: false, plafonReportare: "",
  });
  const [eroare, setEroare] = useState("");
  const [lucreaza, setLucreaza] = useState(false);

  const lipsesc = [
    !f.contractId && "contractul", !f.denumire && "denumirea",
    !f.cantitateInclusa && "cantitatea inclusă",
  ].filter(Boolean);

  async function salveaza() {
    setLucreaza(true); setEroare("");
    try {
      await api.creeazaPachet({
        contractId: f.contractId, denumire: f.denumire, tip: f.tip,
        cantitateInclusa: Number(f.cantitateInclusa), perioada: f.perioada,
        reportabil: f.reportabil,
        // Plafonul se trimite DOAR dacă se reportează: altfel serverul îl
        // refuză, și pe bună dreptate — ar sugera o regulă care nu se aplică.
        ...(f.reportabil && f.plafonReportare
          ? { plafonReportare: Number(f.plafonReportare) } : {}),
      });
      await gata();
    } catch (e) { setEroare(mesajEroare(e)); }
    finally { setLucreaza(false); }
  }

  return (
    <div className="modal-fundal" onClick={inchide}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Pachet inclus în abonament")}</h3>
        {/* Eroarea apare ÎN fereastră: un banner din spatele ei nu se vede. */}
        {eroare && <div className="eroare">{eroare}</div>}

        <label className="camp">
          {tr("contractul")}
          <select value={f.contractId}
            onChange={(e) => setF({ ...f, contractId: e.target.value })}>
            {contracte.map((c) => <option key={c.id} value={c.id}>{c.numar}</option>)}
          </select>
        </label>

        <label className="camp">
          {tr("denumire")}
          <input value={f.denumire} placeholder={tr("ex. Manoperă inclusă")}
            onChange={(e) => setF({ ...f, denumire: e.target.value })} />
        </label>

        <label className="camp">
          {tr("ce include")}
          <select value={f.tip}
            onChange={(e) => setF({ ...f, tip: e.target.value })}>
            {Object.entries(TIPURI_PACHET).map(([v, t]) => (
              <option key={v} value={v}>{tr(t)}</option>
            ))}
          </select>
        </label>

        <label className="camp">
          {tr("cât se include")}
          <input type="number" min="0.01" step="0.01"
            value={f.cantitateInclusa}
            onChange={(e) => setF({ ...f, cantitateInclusa: e.target.value })} />
        </label>

        <label className="camp">
          {tr("pe ce perioadă")}
          <select value={f.perioada}
            onChange={(e) => setF({ ...f, perioada: e.target.value })}>
            {Object.entries(PERIOADE_PACHET).map(([v, t]) => (
              <option key={v} value={v}>{tr(t)}</option>
            ))}
          </select>
        </label>

        <label className="linie-bifa">
          <input type="checkbox" checked={f.reportabil}
            onChange={(e) => setF({ ...f, reportabil: e.target.checked })} />
          {tr("ce nu se consumă trece în perioada următoare")}
        </label>

        {f.reportabil && (
          <label className="camp">
            {tr("plafon de reportare (gol = fără plafon)")}
            <input type="number" min="0" step="0.01"
              value={f.plafonReportare}
              onChange={(e) => setF({ ...f, plafonReportare: e.target.value })} />
          </label>
        )}

        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={inchide}>{tr("renunță")}</button>
          <button className="buton" disabled={lucreaza || lipsesc.length > 0}
            onClick={salveaza}>{tr("salvează")}</button>
        </div>
      </div>
    </div>
  );
}
